import React, { useCallback, useEffect } from 'react'

import { Box, Display, FlexDirection, Heading5 } from '@gousto-internal/citrus-react'

import { IngredientPill } from './IngredientPill'
import { AlternativeOptions } from '../../types'
import { CollectionUUID, RecipeCoreId, RecipeUUID } from '@library/menu-service'
import { queueTrackingEvent } from '@library/tracking-integration'
import {
  IngredientSlot,
  IngredientSlotValue,
} from '@library/menu-service/queries/menuApiMenuRecipe/types'

type TrackSelectRecipeCustomisation = {
  action: 'select_recipe_customisation'
  properties: {
    menu_uuid: string | null
    recipe_uuid?: RecipeUUID
    recipe_variant_uuid: RecipeUUID
    event_screen: string
    customisations_selected?: Record<string, string>
  }
}

type TrackDiscloseRecipeVariants = {
  action: 'disclose_recipe_variants'
  properties: {
    recipe_uuid: RecipeUUID
    menu_uuid: string | null
    collection_id: CollectionUUID
    event_screen: string
    customisations_displayed: Record<string, string[]>
    recipe_variants_uuids_available: RecipeUUID[]
  }
}

type RecipeOptionsIngredientsProps = {
  menuId: string | null
  currentCollectionId: CollectionUUID
  view: string
  baseRecipeId: RecipeCoreId
  recipeUuid: RecipeUUID
  options: AlternativeOptions['options']
  ingredientSlots: AlternativeOptions['ingredientSlots']
  onChangeSelection: (id: RecipeUUID) => void
  closeCustomisationEducation?: () => void
}

export const RecipeOptionsIngredients = ({
  menuId,
  currentCollectionId,
  view,
  baseRecipeId,
  recipeUuid,
  options,
  ingredientSlots,
  onChangeSelection,
  closeCustomisationEducation,
}: RecipeOptionsIngredientsProps) => {
  const baseRecipe = options?.find(({ recipeId }) => recipeId === baseRecipeId)

  const onChange = useCallback(
    (id: RecipeUUID) => {
      const option = options?.find(({ recipe }) => recipe.id === id)
      const customisationsSelected = Object.entries(
        option?.ingredientSlotValues as Record<string, string>,
      ).reduce((acc, [slotId, slotValueId]) => {
        const slot = ingredientSlots?.find(({ id }) => id === slotId) as IngredientSlot
        const value = slot?.values.find(({ id }) => id === slotValueId) as IngredientSlotValue
        return {
          ...acc,
          [slot.name]: value.name,
        }
      }, {})

      closeCustomisationEducation?.()

      queueTrackingEvent<TrackSelectRecipeCustomisation>({
        action: 'select_recipe_customisation',
        properties: {
          menu_uuid: menuId,
          recipe_uuid: baseRecipe?.recipe.id,
          recipe_variant_uuid: id,
          event_screen: view,
          customisations_selected: customisationsSelected,
        },
      })
      onChangeSelection(id)
    },
    [
      baseRecipe?.recipe.id,
      closeCustomisationEducation,
      ingredientSlots,
      menuId,
      onChangeSelection,
      options,
      view,
    ],
  )

  useEffect(() => {
    queueTrackingEvent<TrackDiscloseRecipeVariants>({
      action: 'disclose_recipe_variants',
      properties: {
        recipe_uuid: recipeUuid,
        menu_uuid: menuId,
        collection_id: currentCollectionId,
        event_screen: view,
        customisations_displayed:
          ingredientSlots?.reduce((acc, ingredientSlot) => {
            return {
              ...acc,
              [ingredientSlot.name]: ingredientSlot.values.map(({ name }) => name),
            }
          }, {}) || {},
        recipe_variants_uuids_available: options?.map(({ recipe }) => recipe.id) || [],
      },
    })
  }, [currentCollectionId, ingredientSlots, menuId, options, recipeUuid, view])

  return (
    <Box
      paddingBottom={4}
      display={Display.Flex}
      flexDirection={FlexDirection.Column}
      gap={5}
      style={{ cursor: 'default' }}
    >
      {ingredientSlots?.map((slot) => (
        <Box key={slot.id}>
          <Heading5>{`Choose your ${slot.name}`}</Heading5>
          <Box
            display={Display.Flex}
            flexDirection={FlexDirection.Row}
            gap={3}
            paddingV={2}
            wrap={true}
          >
            {slot.values.map((value) => (
              <IngredientPill
                key={value.id}
                recipeUuid={recipeUuid}
                options={options}
                slotValue={value}
                slotId={slot.id}
                onChangeSelection={onChange}
              />
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  )
}
